import * as React from "react";
import { Calendar as CalendarIcon } from "lucide-react";
import { DateRange } from "react-day-picker";
import { formatDate } from "@/lib/formatDate";
import { useLocale } from "@/hooks/useLocale";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/Button";
import { Calendar } from "@/components/ui/Calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/Popover";
import { useTranslation } from "react-i18next";

interface DateRangeProps {
  className?: string;
  value?: DateRange;
  onUpdate: (value?: DateRange) => void;
  placeholder?: string;
  labeledBy?: string;
}

const DATE_FORMAT = "LLL dd, y";

export const DateRangeInput = ({
  value,
  onUpdate,
  className,
  placeholder,
}: DateRangeProps) => {
  const { t } = useTranslation();
  const locale = useLocale();

  return (
    <div className={cn("grid gap-2", className)}>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            aria-labelledby="date"
            variant={"outline"}
            className={cn(
              "h-9.5 w-full justify-start text-left font-normal",
              !value && "text-muted-foreground",
            )}
          >
            <CalendarIcon />
            {value?.from ? (
              value.to ? (
                <>
                  {formatDate(value.from, DATE_FORMAT, { locale })} -{" "}
                  {formatDate(value.to, DATE_FORMAT, { locale })}
                </>
              ) : (
                formatDate(value.from, DATE_FORMAT, { locale })
              )
            ) : (
              <span>{placeholder || t("global.chooseARange")}</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="range"
            defaultMonth={value?.from}
            selected={value}
            onSelect={onUpdate}
            numberOfMonths={2}
            data-testid="calendar-range"
          />
        </PopoverContent>
      </Popover>
    </div>
  );
};
